import { Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { Suspense, useCallback } from 'react';
import { SidebarProvider, SidebarInset } from '@artstorefronts/ui';
import { NavBar, NavProvider, suiteNavClickEvent, useNav, type NavAppClickArgs } from '@artstorefronts/arthelper-nav';

import { AppSidebar } from '../components/AppSidebar';
import { SiteHeader } from '../components/SiteHeader';
import { ClientQuestBandGate } from '../components/questBand/ClientQuestBandGate';
import { BountyBandGate } from '../components/bountyBand/BountyBand';
import { ProfileProvider } from '../context/profile';
import { MessagingDockProvider } from '../context/messagingDock';
import { MessagingDock } from '../components/messaging/MessagingDock';
import { ClientProfileHoverCardProvider } from '../components/social/profileHoverCardAdapter';
import { NotificationToastListener } from '../components/NotificationToastListener';
import { ResumeOnboardingBanner } from '../components/ResumeOnboardingBanner';
import { PendingRequestsBanner } from '../components/network/PendingRequestsBanner';
import { LiveUpdatesListener } from '../components/LiveUpdatesListener';

import { useQuery } from '@apollo/client';
import {
  IMAGE_QUERY,
  COLLECTION_QUERY,
  SERIES_QUERY,
  NOTIFICATION_COUNTS_QUERY,
  SOCIAL_BADGE_COUNTS_QUERY,
  MY_COMMUNITIES_QUERY,
} from '../graphql/queries';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useNeedsOnboarding } from '~/hooks/useNeedsOnboarding';

import { motion } from 'framer-motion';
import { ImageManagerProvider } from '~/pages/onboarding/context/ImageManagerContext';
import { useConversationChat } from '~/hooks/useConversationChat';
import { useAuth } from '~/context/auth';
import { useAnalytics } from '~/context/analytics';
import { CHROME_BOTTOM_VAR, SITE_HEADER_HEIGHT, SUITE_NAV_HEIGHT, SUITE_NAV_HEIGHT_VAR } from '~/lib/chromeGeometry';
import { DEFAULT_MODEL_ID } from '~/components/Chat/constants';

// Derived from the ArtHelper API base the client already receives, by swapping the
// `graphql` path segment for `rest` — the same idiom as useConversationChat.ts. No new
// environment variable exists for the nav, deliberately.
//
// Deliberately NOT the v1 GraphQL variable (`API_V1_GRAPHQL_URL` in infra, exposed to the
// client as its `VITE_`-prefixed twin). Despite the near-identical name that is the legacy
// ArtStorefronts v1 GraphQL host, consumed only by client/src/context/v1-apollo.tsx.
// Pointing the nav at it would 404 forever while the bar silently lived on its embedded
// fallback — configure-in-data would be dead on this surface with no visible error. Do not
// "fix" this to that variable.
//
// Guarded, never a bare `VITE_API_URL.replace(...)`: this line evaluates at module scope,
// so an unset or misspelled variable in a build would throw
// `Cannot read properties of undefined (reading 'replace')` while importing this layout,
// blanking every authenticated route instead of letting the bar degrade. Same reason
// usePublicChat.ts reaches for `?.replace`. The empty string is deliberate too:
// `fetchConfig` resolves null on it and the bar keeps its embedded fallback, where a
// plausible-looking `undefined/nav-config` would instead read as a real endpoint.
const NAV_CONFIG_URL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL.replace('graphql', 'rest')}/nav-config`
  : '';

function ImagesLayer() {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Extract image ID from art routes (handles /art/123, /art/123/chat, etc.)
  const match = /^\/art\/([^/]+)/.exec(location.pathname);
  const currentImageId =
    match && !searchParams.get('collectionId') && !searchParams.get('seriesId') ? match[1] : undefined;

  const { data: imageData } = useQuery(IMAGE_QUERY, {
    variables: { id: currentImageId },
    skip: !currentImageId,
    fetchPolicy: 'cache-first',
  });

  const collectionId = searchParams.get('collectionId');
  const seriesId = searchParams.get('seriesId');

  const { data: collectionData } = useQuery(COLLECTION_QUERY, {
    variables: { id: collectionId },
    skip: !collectionId,
  });

  const { data: seriesData } = useQuery(SERIES_QUERY, {
    variables: { id: seriesId },
    skip: !seriesId,
  });

  let backgroundImageUrl = '';
  let isVisible = false;

  if (collectionId && collectionData?.collection) {
    const images = collectionData.collection.images;
    const nestedImages = collectionData.collection.nestedImages;
    if (images?.[0]?.optimizedUrl) {
      backgroundImageUrl = images[0].optimizedUrl;
      isVisible = true;
    } else if (nestedImages?.[0]?.optimizedUrl) {
      backgroundImageUrl = nestedImages[0].optimizedUrl;
      isVisible = true;
    }
  } else if (seriesId && seriesData?.series?.images?.[0]?.optimizedUrl) {
    backgroundImageUrl = seriesData.series.images[0].optimizedUrl;
    isVisible = true;
  } else if (currentImageId && imageData?.image) {
    const currentImage = imageData.image;
    if (currentImage.thumbnailUrl) {
      backgroundImageUrl = currentImage.thumbnailUrl;
      isVisible = true;
    }
  }

  if (!backgroundImageUrl) return null;

  return (
    <motion.div
      className="absolute inset-0 overflow-hidden pointer-events-none"
      initial={{ opacity: 0 }}
      animate={{ opacity: isVisible ? 1 : 0 }}
      transition={{ duration: 0.15, easing: 'easeIn' }}
    >
      <div
        className="absolute inset-0 bg-cover bg-center blur-md scale-105 bg-black"
        style={{ backgroundImage: `url(${backgroundImageUrl})` }}
      >
        <div className="absolute inset-0 bg-black opacity-50" />
      </div>
    </motion.div>
  );
}

/**
 * The chrome rows, and the one place the suite bar's live geometry is published.
 *
 * AppSidebar's desktop panel is `position: fixed`, so it is pinned to the viewport and has
 * to be told where the chrome ends — a y coordinate that moved the day the suite bar landed
 * above the SiteHeader, and moves again whenever the artist collapses the bar. The sidebar
 * is a SIBLING of NavProvider rather than a descendant, so it cannot call `useNav()`; the
 * geometry reaches it as two CSS custom properties instead, which inherit down the DOM
 * regardless of positioning (see `~/lib/chromeGeometry` for the consumer-side spelling).
 *
 * The variables go on the chrome ROW rather than on SidebarProvider — the difference from
 * town-square's otherwise identical ChromeShell, which has no equivalent of this route
 * split. NavProvider must wrap whatever node carries them, and here it must also stay
 * inside the `showChrome` condition; hoisting it above SidebarProvider would make
 * SidebarProvider itself remount on every chrome-free round trip (`/my-artwork` -> `/art`
 * -> back) and reset the artist's expanded/collapsed sidebar, which lives in its state. The
 * row is the lowest node that is both inside NavProvider and a DOM ancestor of the sidebar.
 *
 * A plain inline style rather than an effect writing to `document.documentElement`: the
 * collapsed flag comes out of the cookie in NavProvider's `useState` initialiser, so both
 * values are already right on the first paint with no post-mount correction.
 */
function ChromeShell({ children }: { children: React.ReactNode }) {
  const { collapsed } = useNav();

  return (
    <>
      <NavBar />
      <SiteHeader />
      <div
        // `ah-chrome-column` carries the transition that makes the two variables below
        // ANIMATE rather than jump. `--ah-suite-nav-height` is registered as a `<length>`
        // via `@property` in index.css, where the whole mechanism is commented; without
        // that class the sidebar would reach its collapsed offset 180ms ahead of the bar's
        // own height animation and overlap the still-shrinking bar. Named for the column
        // town-square puts it on, deliberately — one class name, one rule, both hosts.
        className="ah-chrome-column flex flex-1 min-h-0"
        style={
          {
            // A collapsed bar leaves layout entirely (`height: 0`), so this is 0px, not
            // 56px. Both endpoints, not the in-between values: the transition interpolates
            // those.
            [SUITE_NAV_HEIGHT_VAR]: collapsed ? '0px' : SUITE_NAV_HEIGHT,
            [CHROME_BOTTOM_VAR]: `calc(var(${SUITE_NAV_HEIGHT_VAR}) + ${SITE_HEADER_HEIGHT})`,
          } as React.CSSProperties
        }
      >
        {children}
      </div>
    </>
  );
}

function AppLayout() {
  const location = useLocation();
  const { user } = useAuth();
  const { amplitude } = useAnalytics();

  // The suite bar's only analytics seam, fired once per activation by the shared handler
  // behind both the desktop tiles and the mobile dropdown — so this covers both surfaces
  // and cannot double-fire. `flushOnExit` for the same reason AppSidebar's portfolio link
  // needs it: the tile is a real cross-app `<a href>`, so this document is replaced and
  // the queued batch would die with it.
  const onAppClick = useCallback(
    (args: NavAppClickArgs) => {
      // The event's name and property shape come from the package, so this host cannot
      // drift from the others and split the fleet-wide grouping on `target_app_id`.
      amplitude.track(...suiteNavClickEvent(args, 'client'));
      amplitude.flushOnExit();
    },
    [amplitude]
  );

  // Check if we're on a chat route
  const isChatRoute = location.pathname === '/chat' || location.pathname.startsWith('/chat/');
  // Broader check used only to hide the messaging dock — also covers the
  // per-artwork chat variants (`/art/chat...`, `/art/:id/chat...`). Anything
  // with a `/chat` path segment counts. We don't reuse this for the chat
  // hook init below because that path is specific to the standalone Arty
  // chat and would mis-initialise on per-artwork chat routes.
  const isAnyChatRoute = /(?:^|\/)chat(?:\/|$)/.test(location.pathname);

  // Poll notification counts so sidebar can read from cache
  useQuery(NOTIFICATION_COUNTS_QUERY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 60000,
  });

  const { enabled: connectionsEnabled } = useFeatureFlag('connections_and_dms');

  // Warm Apollo cache for NavMain / Network badges (AppSidebar reads SOCIAL_BADGE_COUNTS_QUERY with cache-only).
  useQuery(SOCIAL_BADGE_COUNTS_QUERY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 60000,
    skip: !connectionsEnabled,
  });

  // Warm Apollo cache for NavCommunities (AppSidebar reads MY_COMMUNITIES_QUERY with cache-only).
  // Matches town-square's useAppData poll so hasUnseenPosts dots stay in sync.
  useQuery(MY_COMMUNITIES_QUERY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 60000,
    skip: !user,
  });

  // Always initialize chat hook to avoid hook rules violation. Off /chat routes it is disabled:
  // as the Art routes' parent layout, useParams here sees their conversationId too.
  const chatHookAlways = useConversationChat({
    targetType: 'standalone',
    targetId: undefined,
    userId: user?.user_id || user?.id,
    model: DEFAULT_MODEL_ID,
    basePath: '/chat',
    enabled: isChatRoute,
  });

  // Only use the hook on chat routes
  const chatHook = isChatRoute ? chatHookAlways : null;

  const showSidebar =
    [
      '/',
      '/my-artwork',
      '/plans',
      '/billing',
      '/billing/',
      '/integrations',
      '/integrations/instagram/callback',
      '/calendar',
      '/chat',
      '/grow-audience',
      '/grow-your-audience',
      '/art-marketing-autopilot',
      '/messages',
      '/contacts',
      '/network',
      '/notifications',
      // The walkthrough library. It is reached FROM the sidebar's own footer, so a version
      // of this page without the rail would strand somebody one click after they used it.
      '/tutorials',
    ].includes(location.pathname) ||
    location.pathname.startsWith('/chat/') ||
    location.pathname.startsWith('/messages/') ||
    location.pathname.startsWith('/network/');

  const { needsOnboarding } = useNeedsOnboarding();

  const isSaved = location.pathname === '/saved';
  const isArtPage = /^\/art(\/|$)/.test(location.pathname);

  // The one condition that decides whether this route gets app chrome at all. The suite
  // bar and the SiteHeader must render together or not at all: the bar's only restore
  // control is the NavTrigger docked in the header, so a bar without a header would be
  // collapsible with nowhere to dock. Named once, used for both.
  //
  // A half-onboarded user reaches this layout only on the account-management allowlist
  // (`ONBOARDING_ALLOWED_PATHS`), and every nav target in the chrome — sidebar, header,
  // suite bar — is off that list, so `OnboardingRedirect` bounces each one straight back
  // to `/welcome`. Rendering it would offer a dozen dead ends. `/settings` is already
  // chrome-free; this makes `/plans` and `/billing` agree, leaving the resume banner as
  // the only navigation, which is the only one that goes anywhere.
  const showChrome = (showSidebar || isSaved) && !needsOnboarding;
  // The routes the quest and bounty bands appear on: the ones with the app's rail. Not
  // `showChrome` — a half-onboarded member keeps the bands under the resume banner.
  const bandRoute = showSidebar || isSaved;

  const showDock =
    connectionsEnabled &&
    !needsOnboarding &&
    !location.pathname.startsWith('/messages') &&
    !location.pathname.startsWith('/welcome') &&
    !location.pathname.startsWith('/post') &&
    !isArtPage;

  // The one row expression both shells render, so the sidebar and the outlet are spelled
  // once. Chrome-free routes take the same row without the chrome above it and without the
  // custom properties, which is exactly what `CHROME_BOTTOM_CSS`'s fallback assumes.
  const row = (
    <>
      {showChrome && <AppSidebar onNewChat={isChatRoute ? chatHook?.navigateToNewChat : undefined} />}

      <SidebarInset className={`flex min-h-0 w-full min-w-0 flex-1 flex-col ${isArtPage ? 'bg-transparent' : ''}`}>
        <ImagesLayer />
        {/* Inside the inset, not up in the chrome column: AppSidebar is fixed and offset by
            `CHROME_BOTTOM_CSS`, so adding a row above it would leave the sidebar behind. */}
        <ResumeOnboardingBanner />
        {/* Pending connection requests (#4092). Chrome'd routes only: a chrome-free sheet
            such as /settings is a task of its own. Hides itself on /network/*. */}
        {showChrome && <PendingRequestsBanner />}
        {/* The quest band sits static above the page content, same seat as town-square's
            banner (Nick, 2026-09-01). Below the resume banner: on the half-onboarded routes
            that banner is the only navigation that goes anywhere, so it stays topmost.
            Only on the routes that carry the app's rail (Nick, 8 Oct): a full-screen sheet
            such as /settings is a task of its own, and on a phone the bands pushed the
            page itself below the fold. */}
        {/* Bounties sit just above the quest band: one strip, renders nothing while the
            bounties flag is off (#4118). */}
        {bandRoute && <BountyBandGate />}
        {bandRoute && <ClientQuestBandGate />}
        {/* On a phone the collapsed dock is a full-width strip pinned to the bottom, so the
            last row of a page (e.g. a notification's Open chat button) sat under it. Room for
            the strip keeps that row tappable; desktop scrolls inside each page instead. */}
        <div
          className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden md:overflow-y-hidden ${showDock && !isAnyChatRoute ? 'pb-14 md:pb-0' : ''}`}
        >
          <ImageManagerProvider namespace="dashboard">
            <Suspense fallback={null}>
              <Outlet context={{ chatHook: isChatRoute ? chatHook : undefined }} />
            </Suspense>
          </ImageManagerProvider>
        </div>
      </SidebarInset>
    </>
  );

  return (
    <ProfileProvider>
      <MessagingDockProvider>
        <ClientProfileHoverCardProvider>
          <NotificationToastListener />
          <LiveUpdatesListener />
          <SidebarProvider className="flex flex-col h-dvh">
            {/* NavProvider renders no DOM and ChromeShell returns a fragment, so the bar, the
              header and the content row all stay direct flex children of the h-dvh column:
              the bar takes its 56px off the top and the inner scroll container below shrinks
              to match, rather than the page gaining a gap. The provider sits INSIDE the chrome
              condition — that keeps the context present everywhere NavTrigger can render
              (only ever inside SiteHeader) while doing no config fetch on chrome-free
              routes, where a `useNav()` caller would throw anyway. Deliberately not sticky:
              this shell is a fixed-height flex column and the document never scrolls.

              activeAppId is 'home' — the same id town-square passes. ArtHelper client and
              town-square are one product surface, so Home is indicated in both and always
              lands on the town-square community page; app.arthelper.ai is not a nav
              destination and appears nowhere in the catalog. */}
            {showChrome ? (
              <NavProvider activeAppId="home" configUrl={NAV_CONFIG_URL} onAppClick={onAppClick}>
                <ChromeShell>{row}</ChromeShell>
              </NavProvider>
            ) : (
              <div className="flex flex-1 min-h-0">{row}</div>
            )}

            {showDock && <MessagingDock suppressCollapsedMain={isAnyChatRoute} />}
          </SidebarProvider>
        </ClientProfileHoverCardProvider>
      </MessagingDockProvider>
    </ProfileProvider>
  );
}

export default AppLayout;
